<template>
  <div :id="`realEstate-picker-${uniqueId}`" class="realEstate-picker w-100">
    <!-- Single select -->
    <div
        v-if="!multiple"
        dir="ltr"
        class="v-select vs--single vs--searchable w-100 modern-select"
        :class="{'small': small}"
    >
      <div class="vs__dropdown-toggle modern-toggle" @click="toggleModal">
        <!-- Selected item -->
        <div class="vs__selected-options text-truncate">
          <span class="vs__selected text-muted" v-if="uniqueRealEstateName == null">
            {{ msg('None Selected') }}
          </span>
          <span
              class="vs__selected font-weight-500"
              :class="{'text-warning': uniqueRealEstateName.valid !== true}"
              v-else
          >
            {{ uniqueRealEstateName.label }}
          </span>
        </div>

        <!-- Actions -->
        <div class="vs__actions d-flex align-items-center">
          <b-button
              v-if="clearable && uniqueRealEstateName"
              size="sm"
              variant="link"
              class="p-0 mr-1 text-danger"
              @click.stop="clear"
          >
            <feather-icon icon="XIcon" />
          </b-button>
          <feather-icon
              class="cursor-pointer text-primary"
              icon="HomeIcon"
              @click.stop="()=>toggleModal(true)"
          />
        </div>
      </div>
    </div>

    <!-- Multiple select -->
    <div
        v-else
        dir="ltr"
        class="v-select vs--searchable w-100 modern-select"
    >
      <div class="vs__dropdown-toggle modern-toggle" @click="onComboboxClick">
        <div class="vs__selected-options">
          <span class="text-muted" v-if="!multipleRealEstatesNames.length">{{ msg('Empty') }}</span>
          <b-badge
              v-for="realEstate in multipleRealEstatesNames"
              :key="realEstate.id"
              class="mr-1 px-2 py-1 font-weight-500"
              pill
              :variant="realEstate.valid ? 'info' : 'warning'"
          >
            {{ realEstate.label }}
            <button
                v-if="clearable || multipleRealEstatesNames.length > 1"
                type="button"
                class="ml-1 text-light remove-btn"
                @click.stop="()=>removeRealEstate(realEstate.id)"
            >
              <feather-icon icon="XIcon" />
            </button>
          </b-badge>
        </div>

        <div class="vs__actions cursor-pointer">
          <feather-icon class="text-primary" icon="HomeIcon" />
        </div>
      </div>
    </div>

    <!-- Modal -->
    <b-modal
        v-model="modal.active"
        no-fade
        size="lg"
        :id="modalId"
        centered
        :hide-footer="true"
        class="real-estate-modal"
        @show="refresh"
    >
      <template #modal-title>
        <h5 class="mb-0 text-primary">
          <feather-icon icon="HomeIcon" class="mr-1" />
          {{ msg('Select Real Estate') }}
        </h5>
      </template>

      <b-table
          hover
          small
          responsive
          class="real-estate-table-picker mt-2"
          :items="realEstateTableData"
          :fields="fields"
          @row-clicked="onRowClicked"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle mx-1"></b-spinner>
            <strong>{{ msg('Loading') }}...</strong>
          </div>
        </template>

        <template #cell(address)="data">
          <div class="font-weight-500">{{ msg('Room') }} #{{ data.item.apt ? data.item.apt : msg('Unknown') }}</div>
        </template>

        <template #cell(realEstatePrices)="data">
          <span class="text-success">{{ getPrices(data) }}</span>
        </template>

        <template #cell(status)="data">
          <div class="d-flex flex-wrap align-items-center">
            <b-badge :variant="statusVariants(data.value)" class="mr-1 mb-1" pill>
              Status: {{ data.value }}
            </b-badge>
            <b-badge :variant="data.item.nukiStatus ? 'primary' : 'danger'" class="mr-1 mb-1" pill>
              Nuki: {{ data.item.nukiStatus ? msg(data.item.nukiStatus) : 'Kein Gerät' }}
            </b-badge>
            <b-badge :variant="data.item.advertise ? 'success' : 'secondary'" pill>
              Inserat: {{ getFeaturedStatus(data.item) }}
            </b-badge>
          </div>
        </template>
      </b-table>

      <div class="mt-2">
        <b-pagination
            v-model="pagination.page"
            :total-rows="pagination.total"
            :per-page="pagination.amountPerPage"
            align="center"
            @input="refresh"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";

export default {
  name: "realEstatePickerLocker",
  data: () => ({
    uniqueId: 0,
    modal: {
      active: false,
      realEstates: [],
      filter: {
        like: {search: '', name: "", shortDescription: ""},
        eq: {type: "", realEstateArea: {}},
        lte: {},
        gte: {},
        in: {}
      },
    },
    pagination: {
      amountPerPage: 100,
      total: 1,
      number: 1,
    },
    sort: {
      col: "Creationtime",
      order: "desc"
    },
    selected: {
      unique: null,
      multiple: null,
      data: [],
      loading: false,
      error: false,
    }
  }),
  props: {
    value: {
      required: true
    },
    multiple: {
      type: Boolean,
      default: () => false
    },
    clearable: {
      type: Boolean,
      default: () => false
    },
    small: {
      type: Boolean,
      default: () => false
    },
    noBuildingYet: {
      type: Boolean,
      default: () => false
    }, buildingId: {
      type: String,
    },
    buildings: {
      type: Array,
      default: () => []
    }
  },
  watch: {
    value: {
      handler() {
        this.selected.multiple = null;
        this.selected.unique = null;
        if (this.multiple !== true) {
          this.selected.unique = this.value;
        } else {
          if (this.value != null && !Array.isArray(this.value)) {
            this.$emit('input', (!isNaN(this.value) ? [this.value] : []))
            return;
          }
          this.selected.multiple = this.value;
        }
        this.refreshSelected();
      },
      immediate: true
    },
    "modal.filter": {
      handler() {
        this.refresh();
      },
      deep: true
    }
  },
  created() {
    this.uniqueId = this._uid
  },
  computed: {
    ...mapGetters('data', ['getRealEstatesWebSite']),
    ...mapGetters('realEstate', ['allStatus', 'statusVariants', 'getPrices', 'getFeaturedStatus', 'getFullAddress']),
    ...mapGetters('data', ['getUserFullName']),
    fields() {
      let $this = this;
      return [

        {
          key: 'address',
          label: $this.msg('Room'),
          sortable: true,
          active: true,
        },

        {
          key: 'realEstatePrices',
          label: $this.msg('Price'),
          sortable: false,
          active: true,
        },
        {
          key: 'realEstateArea.totalSpace',
          label: $this.msg('Total space'),
          sortable: false,
          active: true,
        }
      ]
    },
    realEstateTableData() {
      let realEstatesMap = (this.modal.realEstates || []).map(rs => ({...rs})).toObject("id");
      let selectedToMerge = [];
      for (let selectedRealEstate of this.selected.data) {
        if (realEstatesMap[selectedRealEstate.id] == null) {
          selectedRealEstate["_rowVariant"] = 'success';
          selectedToMerge.push(selectedRealEstate);
        } else {
          realEstatesMap[selectedRealEstate.id]["_rowVariant"] = 'success';
        }
      }
      return [...selectedToMerge, ...Object.values(realEstatesMap)]
    },
    modalId() {
      return `realEstate-modal-picker-${this.uniqueId}`
    },
    uniqueRealEstateName() {
      if (this.value == null || Array.isArray(this.value) || this.value === "")
        return null;
      if (!Array.isArray(this.selected.data) || this.selected.data.length === 0)
        return {label: `${this.selected.unique} - ${this.msg('Unknown')}`, valid: false};
      if (this.selected.data[0].id !== this.selected.unique) {
        return {label: `${this.selected.unique} - ${this.msg('Unknown')}`, valid: false};
      }
      return {
        label: `${this.msg('Room')}# ${(this.selected.data[0]).apt}`,
        valid: true
      };
    },
    multipleRealEstatesNames() {
      if (!Array.isArray(this.value) || this.value.length === 0)
        return [];
      let currentData = (this.selected.data || []).toObject("id");
      return this.value.map(realEstateId => {
        let realEstate = currentData[realEstateId];
        return {
          label: `${realEstate != null ? realEstate.apt : this.msg('Unknown')}`,
          id: realEstateId,
          valid: realEstate != null,
        }
      });
    }
  },
  methods: {
    ...mapActions('realEstate', ['getAllRealEstates']),
    refreshSelected() {
      let $this = this;
      this.selected.loading = true;
      if (this.value == null || (Array.isArray(this.value) && this.value.length === 0)) {
        this.clear(true);
        this.selected.loading = false;
        return;
      }
      let realEstateIds = Array.isArray(this.value) ? this.value : [this.value];
      if (!this.updateIsRequired(realEstateIds)) {
        this.selected.loading = false;
        return;
      }
      let filter = {in: {id: realEstateIds}};
      this.getAllRealEstates({filter})
          .then(data => {
            $this.selected.data = data['RealEstates'];
          }, () => $this.selected.error = true).finally(() => $this.selected.loading = false);
    },
    updateIsRequired(realEstateIds) {
      if (this.selected.data.length !== realEstateIds.length) {
        return true;
      }
      let currentData = (this.selected.data || []).toObject("id");
      for (let realEstateId of realEstateIds) {
        if (currentData[realEstateId] == null) {
          return true;
        }
      }
      return false;
    },
    clear(force) {
      if (this.clearable !== true && force !== true)
        return;
      this.selected.multiple = null;
      this.selected.unique = null;
      this.selected.data = [];
      this.$emit('input', null)
    },
    removeRealEstate(realEstateId) {
      let currentValue = this.value != null ? [...this.value] : [];
      const index = currentValue.findIndex(rId => rId === realEstateId);
      if (index < 0) {
        return false
      }
      currentValue.splice(index, 1);
      this.selected.data = this.selected.data.filter(realEstate => realEstate.id !== realEstateId);
      this.$emit('input', currentValue);
      return true;
    },

    refresh() {
      let $this = this;
      let filter = {...$this.modal.filter};
      if (Array.isArray(this.buildings) && this.buildings.length > 0) {
        filter.in.buildingId = this.buildings;
      }
      this.getAllRealEstates({
        sortby: $this.sort.order,
        sortcolumn: $this.sort.col,
        page: $this.pagination.page,
        amount: $this.pagination.amountPerPage,
        filter
      }).then(this.onRealEstatesReceived);
    },
    onRealEstatesReceived(data) {
      if (this.noBuildingYet)
          //this.modal.realEstates = data['RealEstates'].filter(rs => rs.buildingId == null|| rs.buildingId===this.buildingId);
        this.modal.realEstates = data['RealEstates'].filter(rs => rs.buildingId == null);
      else
        this.modal.realEstates = data['RealEstates'];
      this.pagination.total = data['Total'];
      this.pagination.page = data['Page'];
      this.$root.$emit('bv::refresh::table')
    },
    toggleModal(status) {
      this.modal.active = status != null ? status : !this.modal.active;
    },
    onComboboxClick(ev) {
      console.log("onComboboxClick", {ev, srcElement: ev.srcElement})
      if (ev.srcElement != null && ev.srcElement.classList.contains("vs__selected-options")) {
        this.toggleModal(true);
      }
    },
    onRowClicked(realEstate) {
      if (this.multiple !== true) {
        this.selected.data = [realEstate];
        this.$emit('input', realEstate.id);
        this.modal.active = false;
        return
      }
      if (this.value == null) {
        this.selected.data = [realEstate];
        this.$emit('input', [realEstate.id]);
        return;
      }
      if (this.removeRealEstate(realEstate.id) === true) {
        return;
      }

      this.selected.data.push(realEstate);
      this.$emit('input', [...this.value, realEstate.id]);
    }
  }
}
</script>

<style lang="scss">
.realEstate-picker {
  .input-group > .realEstate-picker-unique.input-group-append {
    > .btn.realEstate-picker-clear {
      padding-right: 4px;
      padding-left: 4px;
      border: none !important;
    }

    > .btn.realEstate-picker-add {
      border: none !important;
    }

  }
}

.real-estate-table-picker.table.table-sm td {
  padding: 0 0;
}

.realEstate-picker {
  .modern-select {
    .modern-toggle {
      background-color: var(--card-bg, #f8f9fa);
      border: 1px solid rgba(0, 0, 0, 0.08);
      border-radius: 0.6rem;
      padding: 0.5rem 0.75rem;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: all 0.25s ease;
      cursor: pointer;

      &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }

    .vs__selected {
      font-weight: 500;
      color: #333;
    }

    .vs__actions {
      svg {
        width: 18px;
        height: 18px;
      }
    }
  }

  .remove-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
  }

  /* Table */
  .real-estate-table-picker {
    font-size: 0.9rem;

    tbody tr:hover {
      background-color: rgba(0, 123, 255, 0.05);
      cursor: pointer;
    }
  }

  /* === Dark Layout Adaptations === */
  .dark-layout & {
    .modern-toggle {
      background-color: #2a2b32;
      border-color: rgba(255, 255, 255, 0.08);
      color: #eaeaea;

      &:hover {
        background-color: #32343c;
        box-shadow: 0 4px 10px rgba(0, 0, 0, 0.6);
      }
    }

    .vs__selected {
      color: #f2f2f2;
    }

    .real-estate-table-picker {
      background-color: #1e1f24;
      color: #d0d0d0;

      tbody tr:hover {
        background-color: rgba(255, 255, 255, 0.05);
      }
    }

    .b-modal-content {
      background-color: #1e1f24;
      color: #eaeaea;
      border-radius: 0.8rem;
    }

    .b-table thead th {
      color: #bdbdbd;
      border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }
  }
}

</style>
